import { required } from '@seek/validators-js';
import { useTranslations } from '@vocab/react';
import {
  forwardRef,
  type Ref,
  useState,
  useImperativeHandle,
  useEffect,
} from 'react';
import { useDispatch, useSelector, shallowEqual } from 'react-redux';

import { WORKPLACE_OPTION_PRESSED } from 'src/app/shared/dataAnalytics/schema/constants';
import {
  useFeatureToggleData,
  FEATURE_WORKPLACE_OPTIONS,
} from 'src/app/shared/featureToggle';
import { useConfig } from 'src/app/shared/hooks/useConfig';
import { validateField } from 'src/app/utils/validation';
import { workArrangmentDescription } from 'src/app/utils/workArrangement/mapper';
import getTrackingAdapter from 'src/app/v1/dataAnalytics';
import { actionUpdateWorkArrangement } from 'src/app/v1/state/modules/formData/workArrangement';
import { actionUpdateCajaWorkplaceOption } from 'src/app/v1/state/modules/tealium/cajaWorkplaceOption';
import { actionUpdatehasWorkplaceOption } from 'src/app/v1/state/modules/tealium/hasWorkplaceOption';
import type { WorkArrangement } from 'src/graphql/generated';
import type { AppState } from 'src/types/appState';
import type { WorkplaceRef } from 'src/types/formFields';

import translations from './.vocab';
import { WorkplaceView } from './view';

export const Workplace = forwardRef((_props, ref: Ref<WorkplaceRef>) => {
  const { t } = useTranslations(translations);
  const dispatch = useDispatch();

  const { workArrangement } = useSelector(
    (state: AppState) => ({
      workArrangement: state.formData.workArrangement,
    }),
    shallowEqual,
  );

  const [errorMessage, setErrorMessage] = useState<string | null>(null);

  const isWorkplaceOptionFeatureEnabled = useFeatureToggleData(
    FEATURE_WORKPLACE_OPTIONS,
  );

  useEffect(() => {
    if (isWorkplaceOptionFeatureEnabled) {
      dispatch(actionUpdatehasWorkplaceOption(true));
    } else {
      dispatch(actionUpdatehasWorkplaceOption(false));
    }
  }, [isWorkplaceOptionFeatureEnabled, dispatch]);

  const setWorkArrangement = (value?: WorkArrangement) => {
    if (value !== undefined && isWorkplaceOptionFeatureEnabled) {
      dispatch(actionUpdateWorkArrangement(value as WorkArrangement));
      dispatch(actionUpdateCajaWorkplaceOption(value as WorkArrangement));

      const prevSelectedWorkArrangement = workArrangement
        ? workArrangmentDescription(workArrangement)
        : '(none)';
      const currSelectedWorkArrangement = workArrangmentDescription(value);

      const eventData = {
        eventName: WORKPLACE_OPTION_PRESSED,
        objectInteraction: 'job-workplace-option',
        workplaceOptionSelected: `${prevSelectedWorkArrangement}: ${currSelectedWorkArrangement}`,
      };
      getTrackingAdapter.sendLinkEvent(eventData);
      setErrorMessage(null);
    }
  };

  const { getLanguage } = useConfig();

  const handleInputValidation = (
    value: WorkArrangement | null,
    skipIfNull = false,
  ): string | null => {
    const validationResult = validateField(
      {
        field: 'workArrangement',
        validators: [required],
        validatorProps: {
          requiredMessage: t('validation-error'),
        },
        value: value === null ? '' : value,
      },
      skipIfNull,
      getLanguage(),
    );

    setErrorMessage(validationResult);

    return validationResult;
  };

  const [dirty, setDirty] = useState<boolean>(false);
  const handleBlur = () => {
    setDirty(true);
  };
  useEffect(() => {
    if (!dirty) {
      return;
    }
    const result = validateField(
      {
        field: 'workArrangement',
        validators: [required],
        validatorProps: {
          requiredMessage: t('validation-error'),
        },
        value: workArrangement === null ? '' : workArrangement,
      },
      false,
      getLanguage(),
    );
    setErrorMessage(result);
    if (!result) {
      return;
    }

    const eventData = {
      eventName: 'alert_viewed',
      alertShownTone: 'critical',
      alertShownMessage: result,
      alertShownMessageCode: 'no workplace option value',
    };
    getTrackingAdapter.sendLinkEvent(eventData);
  }, [dirty, getLanguage, t, workArrangement]);

  useImperativeHandle(ref, () => ({
    handleValidation: (_skipIfNull = false) =>
      handleInputValidation(workArrangement ?? null, false),
  }));

  return (
    <>
      {isWorkplaceOptionFeatureEnabled && (
        <WorkplaceView
          value={workArrangement}
          setValue={setWorkArrangement}
          errorMessage={errorMessage}
          onBlur={handleBlur}
        />
      )}
    </>
  );
});
